import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Typography, Button, Container, Grid, Paper, Box,
  LinearProgress, List, ListItem, ListItemText, Radio,
  RadioGroup, FormControlLabel, Modal,
  FormControl, FormGroup, Checkbox
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ErrorIcon from '@mui/icons-material/Error';
import HelpIcon from '@mui/icons-material/Help';
import './TakeTest.css';

import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';

const TakeTest = () => {
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [timeLeft, setTimeLeft] = useState(0);
  const [testStarted, setTestStarted] = useState(false);
  const [testCompleted, setTestCompleted] = useState(false);
  const [error, setError] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [openModal, setOpenModal] = useState(false); // State for modal
  const [endTime, setEndTime] = useState(null);
  const [questionTimings, setQuestionTimings] = useState([]);
  const [currentQuestionStartTime, setCurrentQuestionStartTime] = useState(null);
  const [user, setUser] = useState(null);
  const [resumeDialogOpen, setResumeDialogOpen] = useState(false);
  const [inProgressAttempt, setInProgressAttempt] = useState(null);
  const [sessionUuid, setSessionUuid] = useState(null);
  const [questionIds, setQuestionIds] = useState([]); // Question IDs for navigation
  const [questions, setQuestions] = useState({}); // Cache for loaded questions
  const [loadingQuestion, setLoadingQuestion] = useState(false); // Loading state for individual questions
  const { id } = useParams();
  const navigate = useNavigate();

  // Function to fetch a single question on demand
  const fetchQuestion = useCallback(async (questionId) => {
    if (questions[questionId]) {
      return questions[questionId]; // Return cached question
    }

    setLoadingQuestion(true);
    try {
      const response = await axios.get(`/api/tests/${id}/questions/${questionId}`);
      const question = response.data.question;
      setQuestions(prev => ({
        ...prev,
        [questionId]: question
      }));
      setLoadingQuestion(false);
      return question;
    } catch (error) {
      console.error('Error fetching question:', error);
      setLoadingQuestion(false);
      throw error;
    }
  }, [id, questions]);

  // Fetch test data and question IDs
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch test data
        const testRes = await axios.get(`/api/tests/${id}`);
        setTest(testRes.data);
        setTimeLeft(testRes.data.duration * 60); // Convert minutes to seconds

        // Fetch question IDs for navigation
         const questionIdsRes = await axios.get(`/api/tests/${id}/question-ids`);
        const ids = questionIdsRes.data.questionIds;
        setQuestionIds(ids);

        // Fetch user data
        let userObj = null;
        try {
          const userRes = await axios.get('/api/auth/user');
          setUser(userRes.data);
          userObj = userRes.data;
        } catch (userError) {
          console.error('Error fetching user:', userError);
        }

        // Check for in-progress attempt (V2)
        if (userObj) {
          try {
            const token = localStorage.getItem('token');
            const attemptRes = await axios.get(`/api/v2/tests/${id}/in-progress-attempt`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (attemptRes.data && attemptRes.data.success && Array.isArray(attemptRes.data.savedAnswers)) {
              const normalized = {
                answers: attemptRes.data.savedAnswers,
                question_timings: [],
                start_time: attemptRes.data.session?.startTime,
                last_saved_time: attemptRes.data.attempt?.lastActivity
              };
              setInProgressAttempt(normalized);
              setResumeDialogOpen(true);
            }
          } catch (e) {
            // No in-progress attempt found or error
          }
        }

        // Initialize answers array with question IDs
        if (ids && ids.length > 0) {
          const initialAnswers = ids.map(questionId => ({
            questionId: questionId,
            selectedAnswer: null,
            markedForReview: false,
            visited: false
          }));
          setAnswers(initialAnswers);

          // Initialize question timings array
          const initialTimings = ids.map(questionId => ({
            questionId: questionId,
            timeSpent: 0,
            startTime: null,
            endTime: null
          }));
          setQuestionTimings(initialTimings);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching test:', error);
        setError('Failed to load test. Please try again later.');
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  // Fetch question if not in cache
  useEffect(() => {
    const currentQuestionId = questionIds[currentQuestion];
    const question = questions[currentQuestionId];
    if (currentQuestionId && !question && !loadingQuestion) {
      fetchQuestion(currentQuestionId);
    }
  }, [currentQuestion, questionIds, questions, loadingQuestion, fetchQuestion]);

  // Resume dialog handlers
  const handleResume = async () => {
    try {
      if (inProgressAttempt) {
        console.log('Resuming test attempt:', inProgressAttempt);
        
        // Create test session for resumed test
        console.log('Creating test session for resumed test, testId:', id);
        try {
          const token = localStorage.getItem('token');
          const sessionRes = await axios.post(`/api/v2/tests/${id}/start-enhanced`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          console.log('Test session (re)started for resume (V2):', sessionRes.data);
          if (sessionRes?.data?.sessionUuid) {
            setSessionUuid(sessionRes.data.sessionUuid);
          }
        } catch (sessionError) {
          console.error('Error creating session for resume:', sessionError);
          // Continue anyway, as the session might already exist
        }
        
        // Parse answers if it's a string, otherwise use as is
        let parsedAnswers = [];
        if (inProgressAttempt.answers) {
          if (typeof inProgressAttempt.answers === 'string') {
            try {
              parsedAnswers = JSON.parse(inProgressAttempt.answers);
            } catch (e) {
              console.error('Error parsing answers:', e);
              parsedAnswers = [];
            }
          } else {
            parsedAnswers = inProgressAttempt.answers;
          }
        }
        
        // Parse question timings if it's a string, otherwise use as is
        let parsedTimings = [];
        if (inProgressAttempt.question_timings) {
          if (typeof inProgressAttempt.question_timings === 'string') {
            try {
              parsedTimings = JSON.parse(inProgressAttempt.question_timings);
            } catch (e) {
              console.error('Error parsing question timings:', e);
              parsedTimings = [];
            }
          } else {
            parsedTimings = inProgressAttempt.question_timings;
          }
        }
        
        console.log('Parsed answers:', parsedAnswers);
        console.log('Parsed timings:', parsedTimings);
        
        // Validate that we have valid data to resume with
        if (!Array.isArray(parsedAnswers) || !Array.isArray(parsedTimings)) {
          console.error('Invalid data structure for resume');
          setError('Test data is corrupted. Please start fresh.');
          setResumeDialogOpen(false);
          return;
        }
        
        // Ensure we have question IDs loaded
        if (questionIds.length === 0) {
          console.log('No question IDs found, fetching question IDs...');
          try {
            const questionIdsRes = await axios.get(`/api/tests/${id}/question-ids`);
            const ids = questionIdsRes.data.questionIds;
            setQuestionIds(ids);
            
            if (ids.length === 0) {
              setError('No questions found for this test. Please start fresh.');
              setResumeDialogOpen(false);
              return;
            }
          } catch (error) {
            console.error('Error fetching question IDs for resume:', error);
            setError('Failed to load questions. Please start fresh.');
            setResumeDialogOpen(false);
            return;
          }
        }
        
        // Ensure we have the same number of answers/timings as question IDs
        if (questionIds.length > 0) {
          const expectedLength = questionIds.length;
          
          // Pad arrays if they're shorter than expected
          while (parsedAnswers.length < expectedLength) {
            parsedAnswers.push({
              questionId: questionIds[parsedAnswers.length],
              selectedAnswer: null,
              markedForReview: false,
              visited: false
            });
          }
          
          while (parsedTimings.length < expectedLength) {
            parsedTimings.push({
              questionId: questionIds[parsedTimings.length],
              timeSpent: 0,
              startTime: null,
              endTime: null
            });
          }
          
          // Trim arrays if they're longer than expected
          parsedAnswers = parsedAnswers.slice(0, expectedLength);
          parsedTimings = parsedTimings.slice(0, expectedLength);
        }
        
        // Mark questions as visited if they have answers or are marked for review
        const updatedAnswers = parsedAnswers.map(ans => ({
          ...ans,
          visited: ans.selectedAnswer !== null || ans.markedForReview || ans.visited || false
        }));
        
        setAnswers(updatedAnswers);
        setQuestionTimings(parsedTimings);
        setStartTime(inProgressAttempt.start_time);
        setTestStarted(true);
        setResumeDialogOpen(false);
      }
    } catch (error) {
      console.error('Error resuming test:', error);
      setError('Failed to resume test. Please start fresh.');
      setResumeDialogOpen(false);
    }
  };
  const handleStartFresh = () => {
    setResumeDialogOpen(false);
    setTestStarted(false);
    setAnswers([]);
    setQuestionTimings([]);
    setStartTime(null);
    setInProgressAttempt(null);
  };

  // Handle submit test (defined early for useEffect dependency)
  const handleSubmitTest = useCallback(async () => {
    try {
      const questions = test?.questions || test?.QuestionTestMasters || [];
      if (!questions || questions.length === 0) {
        throw new Error('Test data is corrupted');
      }
      
      setTestCompleted(true);
      setEndTime(new Date().toISOString());
  
      // Calculate final time for current question
      if (currentQuestionStartTime) {
        const now = new Date();
        const timeSpent = (now - new Date(currentQuestionStartTime)) / 1000;
  
        setQuestionTimings(prevTimings => {
          const updatedTimings = [...prevTimings];
          const currentTiming = updatedTimings[currentQuestion];
  
          if (currentTiming) {
            currentTiming.timeSpent += timeSpent;
            currentTiming.endTime = now.toISOString();
          }
  
          return updatedTimings;
        });
      }
  
      // Prepare answers array with only questionId and selectedAnswer
      const minimalAnswers = answers.map(ans => ({
        questionId: ans.questionId,
        selectedAnswer: ans.selectedAnswer
      }));

      // Mark attempt as completed
      if (user && test) {
        try {
          if (inProgressAttempt) {
            await axios.post('/api/test-attempts/complete', { attemptId: inProgressAttempt.id });
          }
        } catch (e) {}
      }

      // Submit result
      const resultData = {
        userId: user?.id || null,
        userName: user?.name || 'Guest User',
        testId: test?.id || id,
        answers: minimalAnswers,
        startTime,
        endTime: new Date().toISOString(),
        questionTimings
      };

      try {
        const res = await axios.post('/api/results', resultData);
        console.log('Test submission response:', res.data);
        navigate(`/results/${res.data.result.id}`);
      } catch (error) {
        console.error('Error submitting test results:', error);
        setError('Failed to submit test results. Please try again later.');
      }
  
    } catch (error) {
      console.error('Submission error:', error);
      setError(error.message || 'Failed to submit test. Please try again.');
    }
  }, [answers, currentQuestion, currentQuestionStartTime, id, navigate, questionTimings, startTime, test, user]);
  
  // Timer effect
  useEffect(() => {
    if (!testStarted || testCompleted || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft(prevTime => {
        if (prevTime <= 1) {
          clearInterval(timer);
          handleSubmitTest();
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [testStarted, testCompleted, timeLeft, handleSubmitTest]);

 // Format time as MM:SS
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  // Handle answer selection
  const handleAnswerSelect = async (answer) => {
    const updatedAnswers = [...answers];
    if (!updatedAnswers[currentQuestion]) {
      updatedAnswers[currentQuestion] = {
        questionId: questionIds[currentQuestion],
        selectedAnswer: answer,
        markedForReview: false,
        visited: true,
        isCorrect: false
      };
    } else {
      updatedAnswers[currentQuestion].selectedAnswer = answer;
      updatedAnswers[currentQuestion].visited = true;
    }
    setAnswers(updatedAnswers);
    // Auto-save after answer selection
    if (user && test && sessionUuid) {
      try {
        const token = localStorage.getItem('token');
        await axios.post('/api/v2/tests/save-answer', {
          sessionUuid,
          questionId: questionIds[currentQuestion],
          selectedAnswer: answer,
          answerType: 'single_choice',
          timeSpentSeconds: (questionTimings[currentQuestion]?.timeSpent) || 0,
          isMarkedForReview: false
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (e) {
        // Optionally handle save error
      }
    }
  };

  // Mark for review & next
  const handleMarkForReviewAndNext = async () => {
    const updatedAnswers = [...answers];
    if (!updatedAnswers[currentQuestion]) {
      updatedAnswers[currentQuestion] = {
        questionId: questionIds[currentQuestion],
        selectedAnswer: null,
        markedForReview: true,
        visited: true,
        isCorrect: false
      };
    } else {
      updatedAnswers[currentQuestion].markedForReview = true;
      updatedAnswers[currentQuestion].visited = true;
    }
    setAnswers(updatedAnswers);
    // Auto-save
    if (user && test && sessionUuid) {
      try {
        const token = localStorage.getItem('token');
        await axios.post('/api/v2/tests/save-answer', {
          sessionUuid,
          questionId: questionIds[currentQuestion],
          selectedAnswer: updatedAnswers[currentQuestion]?.selectedAnswer,
          answerType: 'single_choice',
          timeSpentSeconds: (questionTimings[currentQuestion]?.timeSpent) || 0,
          isMarkedForReview: true
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (e) {}
    }
    // Move to next question
    if (currentQuestion < (questionIds?.length || 0) - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  // Update timing for current question before navigating
  const updateQuestionTiming = () => {
    if (currentQuestionStartTime) {
      const now = new Date();
      const timeSpent = (now - new Date(currentQuestionStartTime)) / 1000;
      
      setQuestionTimings(prevTimings => {
        const updatedTimings = [...prevTimings];
        const currentTiming = updatedTimings[currentQuestion];
        
        if (currentTiming) {
          currentTiming.timeSpent += timeSpent;
          currentTiming.endTime = now.toISOString();
        }
        
        return updatedTimings;
      });
    }
  };

  // Palette navigation
  const handlePaletteClick = (idx) => {
    updateQuestionTiming();
    
    // Mark the clicked question as visited
    setAnswers(prevAnswers => {
      const updatedAnswers = [...prevAnswers];
      if (updatedAnswers[idx]) {
        updatedAnswers[idx].visited = true;
      }
      return updatedAnswers;
    });
    
    setCurrentQuestion(idx);
    setCurrentQuestionStartTime(new Date().toISOString());
  };

  // Navigate to next question
  const handleNextQuestion = async () => {
    if (currentQuestion < (questionIds?.length || 0) - 1) {
      // Update timing for current question
      updateQuestionTiming();
      
      // Mark current question as visited
      setAnswers(prevAnswers => {
        const updatedAnswers = [...prevAnswers];
        if (updatedAnswers[currentQuestion]) {
          updatedAnswers[currentQuestion].visited = true;
        }
        return updatedAnswers;
      });
      
      // Move to next question
      setCurrentQuestion(currentQuestion + 1);
      
      // Mark next question as visited
      setAnswers(prevAnswers => {
        const updatedAnswers = [...prevAnswers];
        if (updatedAnswers[currentQuestion + 1]) {
          updatedAnswers[currentQuestion + 1].visited = true;
        }
        return updatedAnswers;
      });
      
      // Set start time for next question
      const now = new Date();
      setCurrentQuestionStartTime(now.toISOString());
      
      // Update timing record for next question
      setQuestionTimings(prevTimings => {
        const updatedTimings = [...prevTimings];
        const nextTiming = updatedTimings[currentQuestion + 1];
        
        if (nextTiming && !nextTiming.startTime) {
          nextTiming.startTime = now.toISOString();
        }
        
        return updatedTimings;
      });
    }
  };

  // Navigate to previous question
  const handlePrevQuestion = async () => {
    if (currentQuestion > 0) {
      // Update timing for current question
      updateQuestionTiming();
      
      // Mark current question as visited
      setAnswers(prevAnswers => {
        const updatedAnswers = [...prevAnswers];
        if (updatedAnswers[currentQuestion]) {
          updatedAnswers[currentQuestion].visited = true;
        }
        return updatedAnswers;
      });
      
      // Move to previous question
      setCurrentQuestion(currentQuestion - 1);
      
      // Mark previous question as visited
      setAnswers(prevAnswers => {
        const updatedAnswers = [...prevAnswers];
        if (updatedAnswers[currentQuestion - 1]) {
          updatedAnswers[currentQuestion - 1].visited = true;
        }
        return updatedAnswers;
      });
      
      // Set start time for previous question (for continued timing)
      const now = new Date();
      setCurrentQuestionStartTime(now.toISOString());
    }
  };

  // Start the test
  const handleStartTest = async () => {
    try {
      // Create test session first
      console.log('Creating test session for testId (V2):', id);
      const token = localStorage.getItem('token');
      const sessionRes = await axios.post(`/api/v2/tests/${id}/start-enhanced`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Test session created (V2):', sessionRes.data);
      if (sessionRes?.data?.sessionUuid) {
        setSessionUuid(sessionRes.data.sessionUuid);
      }
      
      // We already have question IDs from the initial load
      if (questionIds && questionIds.length > 0) {
        const initialAnswers = questionIds.map(questionId => ({
          questionId: questionId,
          selectedAnswer: null,
          markedForReview: false,
          visited: false,
          isCorrect: false
        }));
        setAnswers(initialAnswers);

        const initialTimings = questionIds.map(questionId => ({
          questionId: questionId,
          timeSpent: 0,
          startTime: null,
          endTime: null
        }));
        setQuestionTimings(initialTimings);
      }

      const now = new Date();
      setStartTime(now.toISOString());
      setTestStarted(true);

      setCurrentQuestionStartTime(now.toISOString());

      // Mark first question as visited
      setAnswers(prevAnswers => {
        const updatedAnswers = [...prevAnswers];
        if (updatedAnswers && updatedAnswers.length > 0) {
          updatedAnswers[0].visited = true;
        }
        return updatedAnswers;
      });

      setQuestionTimings(prevTimings => {
        const updatedTimings = [...prevTimings];
        if (updatedTimings && updatedTimings.length > 0) {
          updatedTimings[0].startTime = now.toISOString();
        }
        return updatedTimings;
      });
    } catch (err) {
      console.error('Error starting test:', err);
      setError('Failed to start test. Please try again.');
    }
  };
  
 // Modal handlers
 const handleOpenModal = () => setOpenModal(true);
 const handleCloseModal = () => setOpenModal(false);
 
 const handleEarlySubmit = () => {
    handleSubmitTest();
    handleCloseModal();
  };
  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)'
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><LinearProgress /></Box>;
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Typography variant="h4" color="error" gutterBottom><ErrorIcon /> Error</Typography>
        <p>{error}</p>
        <button className="btn" onClick={() => navigate('/tests')}>Back to Tests</button>
      </Container>
    );
  }

  if (!test) {
    return (
      <Container maxWidth="md">
        <h2>Test Not Found</h2>
        <p>The test you're looking for doesn't exist or has been removed.</p>
        <button className="btn" onClick={() => navigate('/tests')}>Back to Tests</button>
      </Container>
    );
  }

  if (!testStarted) {
    return (
      <>
        <Dialog open={resumeDialogOpen} onClose={handleStartFresh}>
          <DialogTitle>Resume Test?</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              We found an unfinished attempt for this test. Would you like to resume where you left off or start afresh?
            </Typography>
            {inProgressAttempt && (
              <Typography variant="body2" color="text.secondary">
                Last saved: {new Date(inProgressAttempt.last_saved_time).toLocaleString()}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleStartFresh}>Start Afresh</Button>
            <Button onClick={handleResume} color="primary" variant="contained">Resume</Button>
          </DialogActions>
        </Dialog>
        <Container maxWidth="md" sx={{ mt: 4 }}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h4" component="h2" gutterBottom color="#0077b6">
              {test.name}
            </Typography>
            <Typography variant="body1" paragraph>
              {test.description || 'No description available'}
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle1">
                  <AccessTimeIcon sx={{ verticalAlign: 'middle', mr: 1 }} /> Duration: {test.duration} minutes
                </Typography>
              </Grid>
              {test.subjects && test.subjects.length > 0 && (
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle1">
                    Subjects: {test.subjects.join(', ')}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle1">
                  Questions: {questionIds?.length || 0}
                </Typography>
              </Grid>
            </Grid>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                <HelpIcon sx={{ verticalAlign: 'middle', mr: 1 }} /> Instructions
              </Typography>
              <List>
                <ListItem>
                  <ListItemText primary={`The test contains ${questionIds?.length || 0} questions.`} />
                </ListItem>
                <ListItem>
                  <ListItemText primary={`You have ${test.duration} minutes to complete the test.`} />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Each question has only one correct answer." />
                </ListItem>
                <ListItem>
                  <ListItemText primary="You can navigate between questions using the Next and Previous buttons." />
                </ListItem>
                <ListItem>
                  <ListItemText primary="You can review your answers before final submission." />
                </ListItem>
                <ListItem>
                  <ListItemText primary="The test will be automatically submitted when the timer expires." />
                </ListItem>
              </List>
            </Box>
            <Button variant="contained" color="primary" fullWidth onClick={handleStartTest}>
              Start Test
            </Button>
          </Paper>
        </Container>
      </>
    );
  }

  // Test taking screen
  // Calculate question stats
  const totalQuestions = questionIds?.length || 0;
  const answered = answers.filter(a => a && a.selectedAnswer !== null && !a.markedForReview).length;
  const marked = answers.filter(a => a && a.markedForReview).length;
  const notAnswered = answers.filter(a => a && a.selectedAnswer === null && a.visited && !a.markedForReview).length;
  const notVisited = answers.filter(a => a && !a.visited).length;

  if (!questionIds || !Array.isArray(questionIds) || !questionIds.length) {
    return (
      <Container maxWidth="md">
        <Typography variant="h4" color="error" gutterBottom><ErrorIcon /> Error</Typography>
        <p>No questions found for this test.</p>
        <Button variant="outlined" onClick={() => navigate('/tests')}>Back to Tests</Button>
      </Container>
    );
  }

  const currentQuestionId = questionIds[currentQuestion];
  const question = questions[currentQuestionId];

  if (!question || !question.QuestionMaster || !question.QuestionMaster.question) {
    if (loadingQuestion) {
      return (
        <Container maxWidth="md">
          <Typography variant="h4" gutterBottom>Loading Question...</Typography>
          <LinearProgress />
        </Container>
      );
    }
    return (
      <Container maxWidth="md">
        <Typography variant="h4" color="error" gutterBottom><ErrorIcon /> Error</Typography>
        <p>Question data is not available. Please try again later.</p>
        <Button variant="outlined" onClick={() => navigate('/tests')}>Back to Tests</Button>
      </Container>
    );
  }

  return (

   <>
   {/* Early Submit Modal */}
   <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="early-submit-modal"
        aria-describedby="confirm-early-submit"
      >
        <Box sx={{...modalStyle, width: 400, bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 2}}>
          <Typography id="early-submit-modal" variant="h6" component="h2">
            Confirm Early Submission
          </Typography>
          <Typography id="confirm-early-submit" sx={{ mt: 2 }}>
            Are you sure you want to submit the test early?
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={handleCloseModal} sx={{ mr: 2 }}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" onClick={handleEarlySubmit}>
              Submit
            </Button>
          </Box>
        </Box>
      </Modal>

    <div className="take-test">
      <div className="test-header">
        <h2>{test.title}</h2>
        <div className="timer">
          <AccessTimeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Time Remaining: {formatTime(timeLeft)}
        </div>
      </div>

      {/* Main content area with question and right sidebar for palette */}
      <div style={{ display: 'flex', marginBottom: 16 }}>

      {/* Left side - Question container */}
      <div className="question-container" style={{ flex: '1 1 70%', marginRight: '20px' }}>
        <div className="question-number">
          <Typography variant="h6">Question {currentQuestion + 1} of {questionIds?.length || 0}</Typography>
        </div>
        
        <Typography variant="h5" sx={{ mb: 2 }}>{question.QuestionMaster?.question?.question}</Typography>
        {question.QuestionMaster?.question?.image && <img src={question.QuestionMaster.question.image} alt="Question Image" className="question-image" />}
        
        <div className="options-list">
          <FormControl component="fieldset">
            <RadioGroup
              aria-label="options"
              name="options"
              value={answers[currentQuestion]?.selectedAnswer || ''}
              onChange={(e) => handleAnswerSelect(e.target.value)}
            >
              {question.QuestionMaster?.question?.options && question.QuestionMaster.question.options.map((option, index) => (
                <FormControlLabel
                  key={index}
                  value={option}
                  control={<Radio />}
                  label={option}
                  sx={{ display: 'block' }} // Add this line to display options in a column
                />
              ))}
            </RadioGroup>
          </FormControl>
        </div>

        {/* Navigation buttons moved inside question container */}
        <div className="test-controls" style={{ marginTop: '20px' }}>
          <Button 
            variant="outlined" 
            onClick={handlePrevQuestion} 
            disabled={currentQuestion === 0}
          >
            <ArrowBackIcon /> Previous
          </Button>

          <Button
            variant="contained"
            color="info"
            onClick={handleMarkForReviewAndNext}
            sx={{ ml: 2, mr: 2 }}
          >
            Mark for Review & Next
          </Button>

          <Button
            variant="contained"
            color="warning" // Use warning color for early submit
            onClick={handleOpenModal} // Open modal instead of directly submitting
            sx={{ mr: 2 }}
          >
            Early Submit
          </Button>
          {currentQuestion < (questionIds?.length || 0) - 1 ? (
            <Button variant="contained" color="primary" onClick={handleNextQuestion}>
              Next <ArrowForwardIcon />
            </Button>
          ) :(
            <Button variant="contained" color="secondary" onClick={handleSubmitTest}>
              Submit Test
            </Button>
          )}
        </div>
      </div>
      
      {/* Right side - Question palette and summary */}
      <Paper elevation={3} sx={{ flex: '0 0 30%', p: 2, borderRadius: 2, height: 'fit-content' }}>
        <Typography variant="h6" gutterBottom>Question List</Typography>
        
        {/* Question Palette */}
        <div className="palette" style={{ display: 'flex', flexWrap: 'wrap', marginBottom: '20px' }}>
          {(questionIds || []).map((questionId, idx) => {
            const ans = answers[idx];
            let color = '#bdbdbd'; // not visited (default gray)
            
            if (ans) {
              if (ans.markedForReview) {
                color = '#7e57c2'; // purple for marked for review
              } else if (ans.selectedAnswer !== null) {
                color = '#43a047'; // green for answered
              } else if (ans.visited) {
                color = '#e53935'; // red for visited but not answered
              } else {
                // Keep default gray for not visited
                color = '#bdbdbd';
              }
            }
            
            return (
              <Button
                key={questionId}
                onClick={() => handlePaletteClick(idx)}
                style={{ minWidth: 32, minHeight: 32, margin: 2, background: color, color: '#fff', fontWeight: 'bold' }}
                variant={currentQuestion === idx ? 'contained' : 'outlined'}
              >
                {idx + 1}
              </Button>
            );
          })}
        </div>
        
        {/* Status Summary */}
        <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>Status Summary</Typography>
        <Box>
          <span style={{ color: '#43a047', fontWeight: 600 }}>●</span> Answered: {answered}
        </Box>
        <Box>
          <span style={{ color: '#e53935', fontWeight: 600 }}>●</span> Not Answered: {notAnswered}
        </Box>
        <Box>
          <span style={{ color: '#7e57c2', fontWeight: 600 }}>●</span> Marked: {marked}
        </Box>
        <Box>
          <span style={{ color: '#bdbdbd', fontWeight: 600 }}>●</span> Not Visited: {notVisited}
        </Box>
        
        {/* Legend */}
        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>Legend</Typography>
        <Box><span style={{ color: '#43a047', fontWeight: 600 }}>●</span> Answered</Box>
        <Box><span style={{ color: '#e53935', fontWeight: 600 }}>●</span> Visited but Not Answered</Box>
        <Box><span style={{ color: '#7e57c2', fontWeight: 600 }}>●</span> Marked for Review</Box>
        <Box><span style={{ color: '#bdbdbd', fontWeight: 600 }}>●</span> Not Visited</Box>
      </Paper>
      </div>
    </div>
    </>
  );
};

export default TakeTest;
